import { PRODUCT_TYPE_IDS } from "./constants";
import { clearGuestSubscriptionCheckout } from "./guestSubscriptionCheckout";

interface LocalCartItemLike {
  product?: {
    productType?: { id?: number | null } | null;
  } | null;
}

const GUEST_CHECKOUT_UNSUPPORTED_PRODUCT_TYPE_IDS = new Set<number>([
  PRODUCT_TYPE_IDS.DB_ID_PRODUCT_TYPE_GIFT,
  PRODUCT_TYPE_IDS.DB_ID_PRODUCT_TYPE_SUBSCRIPTION,
  PRODUCT_TYPE_IDS.DB_ID_PRODUCT_TYPE_SUBSCRIPTION_PRE_PAID,
]);

const isProductTypeEligibleForGuestCheckout = (productTypeId: number | null | undefined) =>
  productTypeId !== null &&
  productTypeId !== undefined &&
  !GUEST_CHECKOUT_UNSUPPORTED_PRODUCT_TYPE_IDS.has(productTypeId);

const GUEST_BOX_INTENT_KEY = "guestBoxIntent";
const GUEST_DIRECT_ADD_PREFERENCE_KEY = "guestPrefersDirectAdds";
const GUEST_WELCOME_PACK_WARNING_SEEN_KEY = "guestWelcomePackWarningSeen";

// Set when a guest chooses the subscription-box path in the add-bottle tray. Cart
// hydration at login/signup passes it to the backend so the wines land in a first
// subscription box; it is cleared after a confirmed transfer or an explicit one-off
// choice. It's not a subscription until then — locally the box is just the cart.
const GUEST_BOX_INTENT_EVENT = "guestBoxIntentChange";

const hasGuestBoxIntent = () => window.localStorage.getItem(GUEST_BOX_INTENT_KEY) === "true";

const markGuestBoxIntent = () => {
  window.localStorage.setItem(GUEST_BOX_INTENT_KEY, "true");
  window.dispatchEvent(new Event(GUEST_BOX_INTENT_EVENT));
};

const clearGuestBoxIntent = ({ preserveCheckout = false } = {}) => {
  window.localStorage.removeItem(GUEST_BOX_INTENT_KEY);
  if (!preserveCheckout) clearGuestSubscriptionCheckout();
  window.dispatchEvent(new Event(GUEST_BOX_INTENT_EVENT));
};

// localStorage isn't reactive, so mark/clear dispatch a same-tab event for UI
// (e.g. the cart tray toggles) that must follow the intent live. Returns an
// unsubscribe function.
const onGuestBoxIntentChange = (listener: () => void) => {
  window.addEventListener(GUEST_BOX_INTENT_EVENT, listener);
  return () => window.removeEventListener(GUEST_BOX_INTENT_EVENT, listener);
};

// The add-bottle tray asks guests whether a wine starts a subscription box or is a
// one-off buy. Either answer lands the wine in the same local cart, so once they've
// answered once, keep adding straight to it instead of asking again.
const hasGuestChosenDirectAdds = () =>
  window.localStorage.getItem(GUEST_DIRECT_ADD_PREFERENCE_KEY) === "true";

const markGuestDirectAddsChosen = () => window.localStorage.setItem(GUEST_DIRECT_ADD_PREFERENCE_KEY, "true");

// Guests keep the welcome-pack upsell on "Buy it now", but only the first time —
// after that they go straight to the (now public) checkout.
const hasSeenGuestWelcomePackWarning = () =>
  window.localStorage.getItem(GUEST_WELCOME_PACK_WARNING_SEEN_KEY) === "true";

const markGuestWelcomePackWarningSeen = () =>
  window.localStorage.setItem(GUEST_WELCOME_PACK_WARNING_SEEN_KEY, "true");

const getGuestCheckoutCartEligibility = (items: readonly (LocalCartItemLike | null)[]) => {
  const productTypeIds = items.map((item) => item?.product?.productType?.id);
  const hasGiftProduct = productTypeIds.includes(PRODUCT_TYPE_IDS.DB_ID_PRODUCT_TYPE_GIFT);
  const hasUnsupportedProduct = productTypeIds.some(
    (productTypeId) => !isProductTypeEligibleForGuestCheckout(productTypeId)
  );

  return {
    hasGiftProduct,
    hasUnsupportedProduct,
    isEligible: items.length > 0 && !hasUnsupportedProduct,
  };
};

export {
  clearGuestBoxIntent,
  getGuestCheckoutCartEligibility,
  hasGuestBoxIntent,
  hasGuestChosenDirectAdds,
  hasSeenGuestWelcomePackWarning,
  isProductTypeEligibleForGuestCheckout,
  markGuestBoxIntent,
  markGuestDirectAddsChosen,
  markGuestWelcomePackWarningSeen,
  onGuestBoxIntentChange,
};
